// DOM helpers: clone a <template> into the player root, fill data-slot
// placeholders with translated text, and render the various overlays /
// the accept-or-PLUS consent wall.

import { ENV } from './env.js';
import { LANG, T } from './i18n.js';
import { plusUrl } from './env.js';

const root = document.getElementById('player');

function renderTemplate(id, fill) {
  const tpl = document.getElementById(id);
  if (!tpl) return;
  root.replaceChildren(tpl.content.cloneNode(true));
  if (fill) fill(root);
  root.querySelectorAll('[data-action="reload"]').forEach((b) => {
    b.addEventListener('click', () => location.reload());
  });
}

function fillSlots(el, slots) {
  for (const [key, value] of Object.entries(slots)) {
    el.querySelector(`[data-slot="${key}"]`)?.replaceChildren(document.createTextNode(value));
  }
}

export function showError(titleKey, textKey) {
  renderTemplate('tpl-error', (el) => fillSlots(el, {
    title: T[titleKey], text: T[textKey], cta: T.reload,
  }));
}

export function showInfo(titleKey, textKey) {
  renderTemplate('tpl-info', (el) => fillSlots(el, {
    title: T[titleKey], text: T[textKey],
  }));
}

export function showPaywall(assetId) {
  renderTemplate('tpl-paywall', (el) => {
    fillSlots(el, { title: T.paywall_title, text: T.paywall_text, cta: T.paywall_cta });
    el.querySelector('[data-action="checkout"]').href = `${ENV.checkoutUrl}?asset=${assetId}`;
  });
}

export function showLogin() {
  renderTemplate('tpl-login', (el) => {
    fillSlots(el, { title: T.login_title, text: T.login_text, cta: T.login_cta });
    el.querySelector('[data-action="login"]').href = ENV.loginUrl;
  });
}

export function clearRoot() {
  root.replaceChildren();
}

export function showConsentWall(onAccept) {
  renderTemplate('tpl-consent-wall', (el) => {
    fillSlots(el, {
      ads_title:           T.wall_ads_title,
      ads_cta:             T.wall_ads_cta,
      ads_details_summary: T.wall_ads_details_summary,
      ads_details_body:    T.wall_ads_details_body,
      plus_text:           T.wall_plus_text,
      plus_cta:            T.wall_plus_cta,
    });

    // ads_hint with embedded "Privacy Center" link that re-opens the CMP.
    const hintEl = el.querySelector('[data-slot="ads_hint"]');
    if (hintEl) {
      const [pre, post] = T.wall_ads_hint.split('{privacy_center}');
      const link = document.createElement('a');
      link.href = '#';
      link.textContent = T.wall_privacy_center;
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        try { window.__ucCmp?.showFirstLayer(); } catch {}
      });
      hintEl.replaceChildren(document.createTextNode(pre), link, document.createTextNode(post ?? ''));
    }

    el.querySelector('[data-action="plus"]').href = plusUrl(LANG);
    const loginHint = el.querySelector('[data-slot="plus_login"]');
    if (loginHint) {
      const a = document.createElement('a');
      a.href = ENV.loginUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = T.wall_plus_login;
      loginHint.replaceChildren(a);
    }

    el.querySelector('[data-action="accept-all"]').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      try {
        await window.__ucCmp.acceptAllConsents();
      } catch (err) {
        console.error('[embedded] acceptAllConsents rejected:', err);
        btn.disabled = false;
        return;
      }
      // Persist the explicit choice. Remote save may fail in some envs
      // (network/CORS) — that's fine, the local consent is what we need.
      window.__ucCmp.saveConsents('EXPLICIT').catch(() => {});
      // Read the freshly-minted tcString straight from UC. The TCF `tcfapi()`
      // stub only publishes it on the *next* page load in some browsers
      // (Firefox in particular) — so we avoid going through it.
      const tc = (await window.__ucCmp.getConsentDetails())?.consent?.tcString || '';
      onAccept(tc);
    });

    // If the user accepts all through the native CMP (opened via the Privacy
    // Center link), drop our wall too. UC fires `consent_event` (CustomEvent)
    // and `UC_CONSENT` on consent changes — both observed in frontend code.
    const onCmpEvent = async () => {
      const det = await window.__ucCmp.getConsentDetails().catch(() => null);
      if (det && !det.consent.required && det.consent.status === 'ALL_ACCEPTED') {
        window.removeEventListener('consent_event', onCmpEvent);
        window.removeEventListener('UC_CONSENT', onCmpEvent);
        window.removeEventListener('UC_UI_CMP_EVENT', onCmpEvent);
        onAccept(det.consent.tcString || '');
      }
    };
    window.addEventListener('consent_event', onCmpEvent);
    window.addEventListener('UC_CONSENT', onCmpEvent);
    window.addEventListener('UC_UI_CMP_EVENT', onCmpEvent);
  });
}
